<script lang="ts">
import { computed, defineComponent, h, unref } from 'vue';

import { classesStates, classNames } from '@components/utils';

import {
  makeDisabledProps,
  makeGroupItemProps,
  makeReadonlyProps,
  makeTextProps,
  makeTitleProps,
  useDisabled,
  useGroupItem,
  useRender,
  useText,
  useTitle
} from '../../composable';

import { CdsStepsGroupSymbol, useInjectSteps } from './context';

import type { PropType } from 'vue';

export const componentName = 'CdsStepsItem';
export default defineComponent({
  name: componentName,
  props: {
    /**
     * Шаг завершен с ошибкой - для списка и группы
     */
    error: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Шаг выделен - для списка и группы
     */
    warning: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    ...makeGroupItemProps(),
    ...makeReadonlyProps(),
    ...makeDisabledProps(),
    ...makeTitleProps(),
    ...makeTextProps()
  },
  emits: { 'group:selected': (val: { value: boolean }) => true },
  setup(props, { slots }) {
    const { disabledClasses } = useDisabled(props);

    const steps = useInjectSteps();
    const groupItem = useGroupItem(props, CdsStepsGroupSymbol);
    const { hasTitle } = useTitle(props, slots);
    const { hasText } = useText(props, slots);

    const index = computed(() => unref(groupItem.group.items).findIndex((item) => item.id === groupItem.id));
    const selectedIndex = computed(() =>
      unref(groupItem.group.items).findIndex((item) => item.id === unref(groupItem.group.selected)[0])
    );
    const tabindex = computed(() =>
      unref(props.disabled) || unref(props.readonly || !unref(steps.interactive)) ? -1 : 0
    );

    const isWarning = computed(() => unref(props.warning));
    const isError = computed(() => unref(props.error));
    const isDisabled = computed(() => unref(props.disabled));
    const isSelected = computed(() => unref(groupItem.isSelected));
    const isComplete = computed(() => unref(selectedIndex) > unref(index));

    const progressValue = computed(() => {
      if (isComplete.value) return 100;

      if (isSelected.value) return 1;

      return 0;
    });

    const progressStreamStyle = computed(() => {
      if (!unref(steps.vertical))
        return {
          width: `${unref(progressValue)}%`,
          height: '100%'
        };

      return {
        width: `100%`,
        height: `${unref(progressValue)}%`
      };
    });

    const isLast = computed(() => {
      return unref(index) === unref(groupItem.group.items).length - 1;
    });
    const isFirst = computed(() => {
      return unref(index) === 0;
    });

    const slotProps = computed(() => ({
      selected: unref(isSelected),
      disabled: unref(isDisabled),
      complete: unref(isComplete),
      error: unref(isError),
      warning: unref(isWarning),
      last: unref(isLast),
      first: unref(isFirst)
    }));

    const onKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onStepClick();
      }
    };

    const animateSteps = (start: number, end: number, forward: boolean) => {
      const delay = 100;
      let delayMultiplier = 0;

      for (let i = start; forward ? i <= end : i >= end; forward ? i++ : i--) {
        const step = unref(groupItem.group.items)[i];

        if (!step.disabled) {
          const currentDelay = delayMultiplier * delay;

          setTimeout(() => {
            groupItem.group.select(step.id, true);
          }, currentDelay);

          delayMultiplier++;
        }
      }
    };

    const onStepClick = () => {
      if (unref(steps.readonly) || props.readonly) return;

      if (unref(index) === unref(selectedIndex)) return;

      if (unref(index) > unref(selectedIndex)) {
        animateSteps(unref(selectedIndex) + 1, unref(index), true);
      } else {
        animateSteps(unref(selectedIndex) - 1, unref(index), false);
      }
    };

    useRender(() => {
      return h(
        'li',
        {
          class: classNames('cds-steps__item', unref(disabledClasses), {
            'cds-steps__item--complete': unref(isComplete),
            'cds-steps__item--current': unref(isSelected),
            'cds-steps__item--warning': unref(isWarning),
            [classesStates.invalid]: unref(isError)
          }),
          tabindex: unref(tabindex),
          onKeydown,
          onClick: onStepClick
        },
        [
          h(
            'div',
            {
              class: classNames('cds-steps__item-status')
            },
            [
              h('span', { class: 'cds-steps__item-icon' }),
              !unref(isLast) &&
                h(
                  'span',
                  {
                    class: 'cds-steps__item-indicator'
                  },
                  [h('span', { class: 'cds-steps__item-indicator-stream', style: unref(progressStreamStyle) })]
                )
            ]
          ),
          h('div', { class: 'cds-steps__item-content' }, [
            hasTitle &&
              h(
                'span',
                {
                  class: classNames('cds-steps__item-title')
                },
                { default: () => (slots.title ? slots.title(unref(slotProps)) : props.title) }
              ),
            hasText &&
              h(
                'span',
                {
                  class: classNames('cds-steps__item-text')
                },
                { default: () => (slots.text ? slots.text(unref(slotProps)) : props.text) }
              )
          ])
        ]
      );
    });
  }
});
</script>
